import { CardContainer } from "../../components/Card/CardContainer";
import { useNavigate } from "react-router-dom";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { Divider } from "antd";
import StepAntd from "../../components/StepAntd/StepAntd";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { useMessageContext } from "../../context/MessageProvider";
import { resolveShopTierConfig } from "../ShopTier/config";
import { ConfigTierBenefit } from "../ShopTier/mockData";
import Step1, { StartFromOption } from "./CreateShopTierBenefitStep/Step1";
import Step2, { Step2Result } from "./CreateShopTierBenefitStep/Step2";
import Step3 from "./CreateShopTierBenefitStep/Step3";
import { useQuery } from "react-query";
import { createTierProgram, getTierAvailableYear } from "../../datasource/TierProgramDatasource";
import { saveHowItWorksAndTierLevels } from "./saveTierProgramContent";

export interface YearOption {
  label: string;
  value: string;
  year: number;
}

function CreateShopTierBenefit(): JSX.Element {
  const navigate = useNavigate();
  const { messageApi } = useMessageContext();
  const [company] = useLocalStorage("profile", null);
  const config = resolveShopTierConfig(company?.company);
  const [tierProgramId,setTierProgramId] = useState<string | null>(null);

  const { data, isLoading: isAvailableYearLoading } = useQuery(
    ["tier-available-year", company],
    async() => {
      const res = await getTierAvailableYear({ company : company?.company, isHistory: false });
      setTierProgramId(res?.responseData?.latestProgram?.tierProgramId || null);
      return res;
    },
    { enabled: !!company, refetchOnWindowFocus: false },
  );
  const availableYears = data?.responseData?.availableYears;
  const latestProgram = data?.responseData?.latestProgram;
  const [year, setYear] = useState<number>(0);
  const yearsToRender: YearOption[] = useMemo(() => {
    return (Array.isArray(latestProgram) ? latestProgram : []).map((y: any) => {
      return { label: `ปี ${y.year}`, value: String(y.tierProgramId), year: y.year };
    });
  }, [latestProgram]);

  const [startFrom, setStartFrom] = useState<string>("");
  const [step, setStep] = useState<number>(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(() => new Set([0]));
  const [step2Result, setStep2Result] = useState<Step2Result | null>(null);

  useEffect(() => {
    setVisitedSteps((prev) => (prev.has(step) ? prev : new Set(prev).add(step)));
  }, [step]);

  const stepItems = [
    { title: "เลือกปี" },
    { title: "แก้ไขสิทธิประโยชน์" },
    { title: "ตรวจสอบ" },
  ];

  const startFromOptions: StartFromOption[] = [
    {
      label: `คัดลอกจากปีก่อนหน้า`,
      description:
        "นำชุดสิทธิประโยชน์ของปีที่เลือกมาเป็นต้นแบบ แล้วแก้ไขเฉพาะส่วนที่ต้องการปรับเปลี่ยน",
      value: "copy",
    },
    {
      label: "สร้างใหม่ทั้งหมด",
      description:
        "เริ่มจากโครงระดับเปล่า กำหนดสิทธิประโยชน์ใหม่ทุกระดับ เหมาะเมื่อโปรแกรมปีใหม่ต่างจากเดิมทั้งชุด",
      value: "new",
    },
  ];

  const goNext = () => setStep((s) => Math.min(s + 1, stepItems.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const sourceTiers: ConfigTierBenefit[] = [];

  const [isSubmitting, setIsSubmitting] = useState(false);
  // State updates land on the next render, so a fast double-click could pass a state-only check twice.
  const submittingRef = useRef(false);

  const onSubmit = async (data: Step2Result) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await submitProgram(data);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const submitProgram = async (data: Step2Result) => {
    const tierDetail = await createTierProgram(
      company.company,
      availableYears,
      JSON.stringify(data?.faqs!.map((faq) => ({
        title: faq.question,
        detail: faq.answer,
      }))),
      JSON.stringify(data?.rules!.map((rule) => ({
        title: rule.title,
        detail: rule.content,
      }))),
      startFrom === "new" ? "สร้างใหม่ทั้งหมด" : `คัดลอกจากปี ${year}`,
      company?.firstname + " " + company?.lastname,
      data.bannerFile,
      data.initialBannerPath!,
    );
    if (!tierDetail?.responseData) {
      messageApi.error("สร้างชุดสิทธิประโยชน์ไม่สำเร็จ");
      return;
    }
    await saveHowItWorksAndTierLevels(tierDetail.responseData.tierProgramId, data, company);
    navigate("/ShopTier", { replace: true, state: { tab: "config" } });
  }

  const stepsComponents = [
    <Step1
      key={0}
      year={year}
      setYear={setYear}
      setTierProgramId={setTierProgramId}
      isAvailableYearLoading={isAvailableYearLoading}
      yearsToRender={yearsToRender}
      startFrom={startFrom}
      setStartFrom={setStartFrom}
      startFromOptions={startFromOptions}
      onNext={goNext}
    />,
    <Step2
      key={1}
      mode="create"
      config={config}
      sourceTiers={sourceTiers}
      tierProgramId={tierProgramId!}
      startFrom={startFrom}
      company={company.company}
      onNext={(result) => {
        setStep2Result(result);
        goNext();
      }}
      onBack={goBack}
    />,
    <Step3
      key={2}
      mode="create"
      config={config}
      year={availableYears}
      data={step2Result}
      sourceTiers={sourceTiers}
      startFrom={startFrom}
      onBack={goBack}
      onConfirm={() => onSubmit(step2Result!)}
      loading={isSubmitting}
    />,
  ];

  return (
    <CardContainer>
      <PageTitleNested
        title={`สร้างชุดสิทธิประโยชน์ปี ${availableYears}`}
        showBack
        onBack={() => (step === 0 ? navigate(-1) : goBack())}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ตั้งค่าระดับ", path: -1 },
              { text: `สร้างชุดสิทธิประโยชน์ปี ${availableYears}`, path: window.location.pathname },
            ]}
          />
        }
        extra={<StepAntd current={step} items={stepItems} />}
      />
      <Divider />
      {stepsComponents.map((comp, idx) =>
        visitedSteps.has(idx) ? (
          <div key={idx} style={{ display: step === idx ? "block" : "none" }}>
            {comp}
          </div>
        ) : null,
      )}
    </CardContainer>
  );
}

export default CreateShopTierBenefit;
